import { useEffect, useState, useRef } from 'react';
import { useSettlementContext } from '../context/SettlementContext';
import { Box, IconButton, Theme, Typography, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  deleteSettlement,
  getDocumentSettlement,
  getPaymentPreview,
  paySettlement,
} from '../../../../api/paymentsApi/settlementsApi';
import CommonButton from '../../../../components/buttons/CommonButton';
import FormCancelButton from '../../../../components/buttons/FormCancelButton';
import { SETTLEMENTS_EDIT_FORM_DEFAULT_VALUES } from '../../../../helpers/formSchemas/formDefaultValues';
import { settlementsEditFormSchema } from '../../../../helpers/formSchemas/formSchemas';
import usePageTitle from '../../../../hooks/usePageTitle';
import useSnackBar from '../../../../hooks/useSnackBar';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import {
  EntityFlexibleRecord,
  ErrorData,
  SettlementAdditionalInfo,
  SettlementPaymentPreview,
  SettlementDistribution,
  ApprovalFlowDetails,
} from '../../../../types';
import Popup from '../../../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../../../constants/popupDescriptions';
import PaySettlementPopup from '../../../../components/PaySettlementPopup';
import { FieldHeadline } from '../../styled';

import SettlementsDeductionsTable from './SettlementsDeductionsTable';
import SettlementsPayoutCheck from '../SettlementsPayoutCheck';
import SettlementsAttorneyTable, { AttorneyFeeRowKey, isSameAttorneyFee } from './SettlementsAttorneyTable';
import NetSettlementTable from './NetSettlementTable';
import EditDeductionPopup from '../EditDeductionPopup';
import EditNetSettlementPopup from '../EditNetSettlementPopup';
import { getDocument } from '../../../../api/paymentsApi/deductionsApi';
import { AxiosError } from 'axios';
import { printCheck } from '../../../../helpers/printCheck';
import { printCheckInlineStyles } from '../../../../helpers/print-check-inline-styles';
import PrintIcon from '@mui/icons-material/Print';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import clsx from 'clsx';
import WaveSettledAmountHoverCard from '../../../../components/WaveSettledAmountHoverCard';
import moment from 'moment';
import EditAttorneyFeePopup from '../EditAttorneyFeePopup';
import ReviewApprovalSection from './ReviewApprovalSection';

const useStyles = makeStyles()((theme: Theme) => ({
  feesSummaryHeadline: {
    fontFamily: 'Poppins',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    paddingRight: '10px',
  },
  feesSummaryHeader: {
    display: 'flex',
    // justifyContent: 'space-between',
    marginBottom: '10px',
    alignItems: 'center',
    color: theme.palette.common.white,

    button: {
      p: {
        fontFamily: 'Poppins',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
      },
    },
  },
  feesSummaryHeaderPrintBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  printButton: {
    color: 'white',
    padding: 0,

    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
  },
  pageContainer: {
    display: 'flex',
    width: '100%',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
  },
  topRowContainer: {
    display: 'flex',
    gap: '24px',
    width: '90%',
    alignItems: 'stretch',

    '@media (max-width: 1200px)': {
      flexDirection: 'column',
      flexBasis: '500px',
      flexShrink: 1,
      minWidth: 0,
      alignItems: 'stretch',
    },
  },
  leftColumnsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    paddingBottom: '1px',
  },
  formLeftColumnContainer: {
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.primary.light,
    borderRadius: '16px',
    flexShrink: 1,
    minWidth: 0,

    '@media (max-width: 1200px)': {
      flexShrink: 'inherit',
      minWidth: 'inherit',
    },

    '@media (max-width: 650px)': {
      padding: '16px',
    },
  },
  formLeftColumnContainerSettledAmount: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    background: theme.palette.primary.light,
    borderRadius: '16px',
    padding: '20px',
    gap: '12px',
    flexShrink: 1,
    minWidth: 80,

    '@media (max-width: 1200px)': {
      flexShrink: 'inherit',
      minWidth: 'inherit',
    },

    '@media (max-width: 650px)': {
      padding: '16px',
    },
  },
  leftColumnsWrapperAmount: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    paddingBottom: '1px',
  },
  summaryWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
    width: '100%',

    '@media (max-width: 1200px)': {
      flexDirection: 'column',
      flexBasis: '500px',
      flexShrink: 1,
      minWidth: 0,
    },
  },
  formLeftDeductionContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '95%',
    background: theme.palette.primary.light,
    borderRadius: '16px',
    padding: '20px',
    flexShrink: 1,

    '@media (max-width: 1200px)': {
      flexShrink: 'inherit',
      minWidth: 'inherit',
    },

    '@media (max-width: 650px)': {
      padding: '16px',
    },
  },
  formCheckDetailsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  formActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '168px',
    maxWidth: '200px'
  },
  formReviewContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '168px',
    maxWidth: '500px'

    // '@media (max-width: 1200px)': {
    //   maxWidth: '450px',
    // },
  },
  accordionRoot: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Poppins',
    '&.MuiAccordion-root, &.MuiPaper-root': {
      borderRadius: '16px !important',
    },
    '&:first-of-type, &:last-of-type': {
      borderRadius: '16px !important',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Poppins',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },
    flexDirection: 'row-reverse',
    '@media print': {
      '& .MuiAccordionSummary-expandIconWrapper': {
        display: 'none',
      },
      '& .MuiAccordionSummary-content': {
        marginRight: 0,
      },
    },
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: '#2e3243',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1), // reduced gap
    fontFamily: 'Poppins',
  },
  summaryBox: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: '16px',
  },

  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  summaryBalanceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 14,
    marginTop: theme.spacing(1),
    // padding: '16px',
  },


  summaryLabel: {
    fontFamily: 'Poppins',
    fontSize: 14,
    color: theme.palette.text.primary,
  },

  summaryValue: {
    fontFamily: 'Poppins',
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },

  summaryValueWarning: {
    fontFamily: 'Poppins',
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.warning.main,
  },

  iotaBalanceValue: {
    fontFamily: 'Poppins',
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },

  subtitleText: {
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },

  // Settlement Preview Card Styles
  settlementRowBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 8px 0 0',
  },
  settlementLabelPrimary: {
    fontSize: '18px',
    fontWeight: 600,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  settlementValuePrimary: {
    fontSize: '18px',
    fontWeight: 800,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  settlementLabelSecondary: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  settlementValueSecondary: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  settlementDivider: {
    height: '2px',
    background: theme.palette.secondary.dark,
    marginTop: '10px',
    marginBottom: '10px',
    marginLeft: '10px',
  },
  settlementInfoIconBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 0,
    paddingLeft: 0,
  },
  settlementBalanceLabel: {
    fontSize: '15px',
    fontWeight: 600,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  settlementBalanceValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },

  // Styles for Holdback Accordion sections
  feesSummarySectionContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  feesSummarySectionHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 8px 10px 10px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Poppins',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 'normal',
    },
  },
  feesSummarySectionInnerHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px 6px 34px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
    },
  },
  feesSummaryBlockDivider: {
    height: '1px',
    background: theme.palette.secondary.dark,
    marginBottom: '10px',
    marginLeft: '10px',
  },
  feesSummaryBlockDividerFull: {
    height: '1px',
    background: theme.palette.primary.main,
    marginLeft: '-16px',
    marginRight: '-16px',
  },

  // Inline styles moved to classes
  infoIconClickableBox: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  infoIcon: {
    fontSize: 16,
    color: 'white',
  },
  expandIconWhite: {
    color: 'white',
  },
  accordionHeaderBox: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
  },
  accordionHeaderInnerBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  holdbackTitle: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  holdbackAmount: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  sectionHeadingText: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  sectionAmountText: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  innerItemText: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
  },
  innerItemLabel: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  innerItemWarning: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.warning.main,
  },
  innerItemAmount: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.warning.main,
  },
  amountAligned: {
    minWidth: '120px',
    textAlign: 'right',
    flexShrink: 0,
  },
  ioltaBalanceLabel: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  sectionsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  printHeaderHidden: {
    display: 'none',
  },
  errorMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '16px',
    gap: '12px',
    background: theme.palette.primary.light,
    width: '100%',
    margin: '12px 0',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    wordBreak: 'break-word',
  },

  errorMessageText: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.error.main,
    margin: '7px'
  },

  warningMessageText: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.warning.main,
    margin: '7px'
  },

}));

const SettlementsEditFormContainer = () => {
  const navigate = useNavigate();
  const styles = useStyles();
  const { refreshSettlement, setPaymentPreviewSummary, registerPaymentPreviewRefresh } = useSettlementContext();
  const currentDateTime = moment().format('hh:mm A, M/D/YYYY').split(',');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { settlementId } = useParams();
  const { setPageTitle } = usePageTitle();
  const { setAlert } = useSnackBar();

  const canEdit = useHasPermission('Payments', 'Edit');
  const canCreate = useHasPermission('Payments', 'Create');
  const canExecutePayment = useHasPermission('Payments', 'Execute Payment');
  const canDelete = useHasPermission('Payments', 'Delete');
  const { entityData } = useOutletContext<EntityFlexibleRecord>();
  const isOnboardingReadOnly = Boolean(entityData?.hasActiveOnboarding);
  const onboardingLockedTooltip = 'This is disabled. See the details page for more information.';
  const canEditSettlement = canEdit && !isOnboardingReadOnly;
  const canCreateSettlement = canCreate && !isOnboardingReadOnly;

  const [payoutCheck, setPayoutCheck] = useState<SettlementPaymentPreview>();

  const [approvalFlowDetails, setApprovalFlowDetails] = useState<ApprovalFlowDetails>();
  const canInitiatePayment = !approvalFlowDetails?.canInitiatePayment && !approvalFlowDetails?.canOverride;
  const settlementDetails = {
    settlementAmount: (payoutCheck?.currentDisbursement as number) || 0,
    caseName: (entityData?.caseSystemName as string) || '',
  }
  const approvalRequired = approvalFlowDetails?.approvalRequired as boolean;

  const [deductionTotal, setDeductionTotal] = useState<{
    total: number;
    liens: number;
    liensWithheld: number;
    liensNegotiationSavings: number;
    advances: number;
    advancesWithheld: number;
    advancesNegotiationSavings: number;
    serviceProviders: number;
    serviceProvidersWithheld: number;
    serviceProvidersNegotiationSavings: number;
    totalPaid: number;
    totalWithheld: number;
    totalNegotiationSavings: number;
    totalLien: number;
    totalAdvance: number;
    totalServiceProvider: number;
  }>({
    total: 0,
    liens: 0,
    liensWithheld: 0,
    liensNegotiationSavings: 0,
    advances: 0,
    advancesWithheld: 0,
    advancesNegotiationSavings: 0,
    serviceProviders: 0,
    serviceProvidersWithheld: 0,
    serviceProvidersNegotiationSavings: 0,
    totalPaid: 0,
    totalWithheld: 0,
    totalNegotiationSavings: 0,
    totalLien: 0,
    totalAdvance: 0,
    totalServiceProvider: 0,
  });

  const [settlementSummary, setSettlementSummary] = useState<{
    attorneysFeesTotal: number;
    netSettlementTotal: number;
    grossSettlement: number;
    total: number;
    liens: number;
    liensWithheld: number;
    advances: number;
    advancesWithheld: number;
    serviceProviders: number;
    serviceProvidersWithheld: number;
  }>({
    attorneysFeesTotal: 0,
    netSettlementTotal: 0,
    grossSettlement: 0,
    total: 0,
    liens: 0,
    liensWithheld: 0,
    advances: 0,
    advancesWithheld: 0,
    serviceProviders: 0,
    serviceProvidersWithheld: 0,
  });
  const [attorneyFeesTotal, setAttorneyFeesTotal] = useState<number>(0);
  const [currentWave, setCurrentWave] = useState<number>(0);

  const [isPayoutCheckUpdating, setIsPayoutCheckUpdating] = useState<boolean>(true);
  const [settledAmount, setSettledAmount] = useState<string>('');
  const [additionalSettlementInfo, setAdditionalSettlementInfo] = useState<SettlementAdditionalInfo>(); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [currentCaseId, setCurrentCaseId] = useState<string>('');
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState<boolean>(false);
  const [isPaySettlementPopupOpen, setIssPaySettlementPopupOpen] = useState<boolean>(false);
  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);

  const handleOpenDeletePopup = () => {
    if (isOnboardingReadOnly) {
      return;
    }
    setIsDeletePopupOpen(true);
  };
  const handleCloseDeletePopup = () => setIsDeletePopupOpen(false);
  const handleOpenPaySettlementPopup = () => setIssPaySettlementPopupOpen(true);
  const handleClosePaySettlementPopup = () => setIssPaySettlementPopupOpen(false);

  const [isEditDeductionPopupOpen, setIsEditDeductionPopupOpen] = useState<boolean>(false);
  const [selectedDeductionIndex, setSelectedDeductionIndex] = useState<number | null>(null);
  const [isEditNetSettlementPopupOpen, setIsEditNetSettlementPopupOpen] = useState<boolean>(false);

  const [isEditAttorneyFeePopupOpen, setIsEditAttorneyFeePopupOpen] = useState<boolean>(false);
  const [selectedAttorneyIndex, setSelectedAttorneyIndex] = useState<AttorneyFeeRowKey | null>(null);

  const [hoverCardAnchorEl, setHoverCardAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredWave, setHoveredWave] = useState<SettlementDistribution | null>(null);
  const hoverCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Accounting sync state lifted from child tables
  const [attorneyFeesSyncMap, setAttorneyFeesSyncMap] = useState<Record<number, boolean>>({});
  const [netSettlementSynced, setNetSettlementSynced] = useState<boolean>(false);
  const [isOpenConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);

  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [isSummaryAccordionOpen, setIsSummaryAccordionOpen] = useState(true);


  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(settlementsEditFormSchema),
    defaultValues: SETTLEMENTS_EDIT_FORM_DEFAULT_VALUES,
  });

  const {
    reset,
    watch,
    control,
    formState: { isDirty },
    setValue
  } = formMethods;

  // Use useFieldArray for managing dynamic arrays - this gives you both fields and values
  const { fields: deductionFields } = useFieldArray({
    name: 'deduction',
    control,
  });

  const deductionsFields = watch('deduction');
  const netSettlementConfig = entityData?.config;
  const attorneysFeesFields = watch('attorneysFees');
  const netSettlementFields = watch('netSettlement') as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  const netSettlementIntent = netSettlementFields?.disposition;
  const currentPlaintiffPaymentAmount = payoutCheck?.distributions?.reduce((current, distribution) =>
    distribution.sequence > current.sequence ? distribution : current
  )?.netSettlement?.amount;

  const handlePaySettlementConfirmation = () => {
    setOpenConfirmationPopup(false);
    handleOpenPaySettlementPopup();
  };

  // Initialize attorney fee sync map from form data when data first loads
  useEffect(() => {
    if (Array.isArray(attorneysFeesFields) && attorneysFeesFields.length > 0) {
      const map: Record<number, boolean> = {};
      (attorneysFeesFields as any[]).forEach((f: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (f?.id != null) map[f.id] = f.accounting?.isSynced ?? false;
      });
      setAttorneyFeesSyncMap(map);
    }
  }, [JSON.stringify((attorneysFeesFields as any[])?.map((f: any) => f?.id))]); // eslint-disable-line @typescript-eslint/no-explicit-any

  // Initialize net settlement sync state from form data
  useEffect(() => {
    if (netSettlementFields?.accounting != null) {
      setNetSettlementSynced(netSettlementFields.accounting.isSynced ?? false);
    }
  }, [netSettlementFields?.accounting?.isSynced]); // eslint-disable-line react-hooks/exhaustive-deps

  // Blocks Pay Settlement when accounting is enabled and any pay-enabled entity is unsynced
  const isAccountingSyncBlocked = (() => {
    // Deductions: isAdded + has accounting + not Withhold → must be synced (reads form data, updated on popup save)
    const deductionBlocked = Array.isArray(deductionsFields) && (deductionsFields as any[]).some((d: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!d?.isAdded) return false;
      if (!d?.accounting) return false;
      if (d?.disposition === 'Withhold') return false;
      return !d?.accounting?.isSynced;
    });

    // Attorney fees: has accounting + not Withhold → must be synced (uses live syncMap from child)
    const attorneyBlocked = Array.isArray(attorneysFeesFields) && (attorneysFeesFields as any[]).some((f: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!f?.accounting) return false;
      if (f?.disposition === 'Withhold') return false;
      return !attorneyFeesSyncMap[f?.id];
    });

    // Net settlement: has accounting + not Withhold → must be synced (uses live state from child)
    const netBlocked = (() => {
      if (!netSettlementFields?.accounting) return false;
      if ((netSettlementFields?.disposition as string)?.toLowerCase() === 'withhold') return false;
      return !netSettlementSynced;
    })();

    return deductionBlocked || attorneyBlocked || netBlocked;
  })();


  const handleDeductionMenuOptionClick = async (option: string, index: number, fieldIndex: number) => {
    if (option === 'Edit') {
      if (isOnboardingReadOnly) {
        return;
      }
      setSelectedDeductionIndex(fieldIndex);
      setIsEditDeductionPopupOpen(true);
    }
    if (option === 'Download Invoice') {
      getDocument(index.toString())
        .then((response) => {
          if (response.data?.downloadUrl) {
            const fileUrl = response.data.downloadUrl;
            // Trigger download
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = ''; // Browser will use the filename from the URL
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            setAlert((prev) => ({
              ...prev,
              message: 'No file found to download.',
              type: 'info',
            }));
          }
        })
        .catch((error: AxiosError<ErrorData>) => {
          if (error.response?.data.message) {
            setAlert((prev) => ({
              ...prev,
              message: error.response?.data.message || 'No File found to download.',
              type: 'info',
            }));
          } else {
            setAlert((prev) => ({
              ...prev,
              message: error.response?.data.message || 'File download error',
              type: 'error',
            }));
          }
        });
    }
  };

  const handleNetSettlementMenuOptionClick = (option: string) => {
    if (option === 'Edit') {
      if (isOnboardingReadOnly) {
        return;
      }
      setIsEditNetSettlementPopupOpen(true);
    }
  };

  const handleAttorneyMenuOptionClick = (option: string, row: AttorneyFeeRowKey) => {
    if (option === 'Edit') {
      if (isOnboardingReadOnly) {
        return;
      }
      setSelectedAttorneyIndex(row);
      setIsEditAttorneyFeePopupOpen(true);
    }
  };

  const handleCalculatePayoutInfo = () => {
    if (!settlementId) {
      return;
    }

    // Only show spinner if we don't have data yet
    if (!payoutCheck) {
      setIsPayoutCheckUpdating(true);
    }

    getPaymentPreview(Number(settlementId))
      .then((res) => {

        if (res.data) {
          setPayoutCheck(res.data);
          setPaymentPreviewSummary({
            settlementType: res.data.settlementType,
            settlementAmount: res.data.settlementAmount,
            pipAmount: res.data.pipAmount,
            lienReimbursementAmount: res.data.lienReimbursementAmount,
          });
          setApprovalFlowDetails({
            canOverride: res.data.canOverride,
            approvalTimeline: res.data.approvalTimeline,
            payGateStatus: res.data.payGateStatus,
            blockingReason: res.data.blockingReason,
            blockingReasonCode: res.data.blockingReasonCode,
            activeCycleStatus: res.data.activeCycleStatus,
            activeCycleNo: res.data.activeCycleNo,
            approvalRequired: res.data.approvalRequired,
            approvals: res.data.approvals,
            requestChanges: res.data.requestChanges,
            pendingApprovals: res.data.pendingApprovals,
            canInitiatePayment: res.data.canInitiatePayment,
          });
          // Get the distribution with highest sequence
          const currentDistribution = res.data.distributions?.reduce((prev, current) =>
            (current.sequence > prev.sequence) ? current : prev
          );
          setCurrentWave(res.data.distributions?.length ?? 0);
          setDeductionTotal({
            total: currentDistribution?.deductions?.total ?? 0,
            liens: currentDistribution?.deductions?.liens ?? 0,
            liensWithheld: currentDistribution?.deductions?.liensWithheld ?? 0,
            liensNegotiationSavings: currentDistribution?.deductions?.liensNegotiationSavings ?? 0,
            advances: currentDistribution?.deductions?.advances ?? 0,
            advancesWithheld: currentDistribution?.deductions?.advancesWithheld ?? 0,
            advancesNegotiationSavings: currentDistribution?.deductions?.advancesNegotiationSavings ?? 0,
            serviceProviders: currentDistribution?.deductions?.serviceProviders ?? 0,
            serviceProvidersWithheld: currentDistribution?.deductions?.serviceProvidersWithheld ?? 0,
            serviceProvidersNegotiationSavings: currentDistribution?.deductions?.serviceProvidersNegotiationSavings ?? 0,
            totalPaid: currentDistribution?.deductions?.totalPaid ?? 0,
            totalWithheld: currentDistribution?.deductions?.totalWithheld ?? 0,
            totalNegotiationSavings: currentDistribution?.deductions?.totalNegotiationSavings ?? 0,
            totalLien: currentDistribution?.deductions?.totalLien ?? 0,
            totalAdvance: currentDistribution?.deductions?.totalAdvance ?? 0,
            totalServiceProvider: currentDistribution?.deductions?.totalServiceProvider ?? 0,
          });
          setAttorneyFeesTotal(currentDistribution?.attorneysFees?.total ?? 0);
          setSettlementSummary({
            attorneysFeesTotal: currentDistribution?.attorneysFees?.total ?? 0,
            netSettlementTotal: currentDistribution?.netSettlement?.amount ?? 0,
            grossSettlement: 0,
            total: currentDistribution?.deductions?.total ?? 0,
            liens: currentDistribution?.deductions?.liens ?? 0,
            liensWithheld: currentDistribution?.deductions?.liensWithheld ?? 0,
            advances: currentDistribution?.deductions?.advances ?? 0,
            advancesWithheld: currentDistribution?.deductions?.advancesWithheld ?? 0,
            serviceProviders: currentDistribution?.deductions?.serviceProviders ?? 0,
            serviceProvidersWithheld: currentDistribution?.deductions?.serviceProvidersWithheld ?? 0,
          });
          setTimeout(() => {
            setIsPayoutCheckUpdating(false);
          });
        }
      })
      .catch((err) => {
        setErrorMessage(err.response?.data?.message);
        setIsPayoutCheckUpdating(false);
        // Clear payoutCheck to force error state display
        setPayoutCheck(undefined);
      });
  };

  // Initial load with deductions from entityData
  useEffect(() => {
    if (!settlementId || !entityData || hasInitialLoad) {
      return;
    }
    handleCalculatePayoutInfo();
    setHasInitialLoad(true);
  }, [settlementId, entityData]);

  // Let the parent view trigger a payout preview refresh (e.g. after editing amounts)
  useEffect(() => {
    registerPaymentPreviewRefresh(handleCalculatePayoutInfo);
  });

  const handleDeleteSettlement = () => {
    if (!settlementId) {
      return;
    }

    deleteSettlement(Number(settlementId))
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Settlement deleted successfully.',
          type: 'success',
        }));
        navigate('/payments/settlements');
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
        handleCloseDeletePopup();
      });
  };

  const handleProcessSettlement = (pin: string) => {
    if (!settlementId) {
      return;
    }

    paySettlement(Number(settlementId), pin)
      .then((res) => {
        if (res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'The settlement payment process has been initiated.',
            type: 'success',
          }));
          refreshSettlement();
          setTimeout(() => {
            navigate(`/payments/settlement/${settlementId}/details`, {
              state: { delayApiCall: true, delayMs: 2000 }
            });
          }, 500);
        }
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleCancelChanges = () => {
    reset();
    handleCalculatePayoutInfo();
  };

  const getSelectedDeductionData = () => {
    if (selectedDeductionIndex !== null && deductionsFields && deductionsFields[selectedDeductionIndex]) {
      return deductionsFields[selectedDeductionIndex];
    }
    return null;
  };

  const getSelectedAttorneyFeeData = () => {
    if (selectedAttorneyIndex !== null && attorneysFeesFields) {
      const selectedAttorney = attorneysFeesFields.find((fee) => isSameAttorneyFee(fee, selectedAttorneyIndex));
      if (selectedAttorney) {
        console.log('Selected Attorney Fee Data:', selectedAttorney); // Debugging log
        return selectedAttorney;
      }
    }
    return null;
  };

  const handleViewDocument = async () => {
    const documentKey = payoutCheck?.docpath;
    if (!documentKey) {
      setAlert((prev) => ({
        ...prev,
        message: 'Document not found.',
        type: 'info',
      }));
      return;
    }

    try {
      const encodedDocumentKey = encodeURIComponent(documentKey as string);
      const response = await getDocumentSettlement(encodedDocumentKey);
      if (response.data?.downloadUrl) {
        const fileUrl = response.data.downloadUrl;
        // Trigger download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = ''; // Browser will use the filename from the URL
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: response.data?.message || 'No file found to view.',
          type: 'info',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'File view error',
        type: 'error',
      }));
    }
  };

  const handlePaySettlement = () => {
    if (isOnboardingReadOnly) {
      return;
    }
    const showUserOnboardingDetails =
      localStorage.getItem('showUserOnboardingDetails') === 'true';

    if (
      !showUserOnboardingDetails &&
      netSettlementIntent === 'PAY' &&
      currentPlaintiffPaymentAmount !== 0
    ) {
      setOpenConfirmationPopup(true);
      return;
    }

    handleOpenPaySettlementPopup();
  };

  const handleHoverCardOpen = (event: React.MouseEvent<HTMLDivElement>, wave: SettlementDistribution) => {
    // Clear any pending close timeout
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
    setHoverCardAnchorEl(event.currentTarget);
    setHoveredWave(wave);
  };

  const handleHoverCardClose = () => {
    // Add a small delay before closing to prevent flickering
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoverCardAnchorEl(null);
      setHoveredWave(null);
    }, 100);
  };

  const handleHoverCardMouseEnter = () => {
    // Cancel any pending close when hovering over the popover
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  };

  // Refresh settlement data on component mount to ensure fresh data
  useEffect(() => {
    refreshSettlement();
  }, []);

  useEffect(() => {
    if (entityData) {
      setSettledAmount(entityData.settledAmount as string);
      setCurrentCaseId(entityData.caseId as string);
      setPageTitle && setPageTitle('Settlement');
      reset({
        ...entityData,
      });
    }

    return () => setPageTitle && setPageTitle('');
  }, [entityData]);


  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    };
  }, []);

  // Get the distribution with the highest sequence number
  const currentDistribution = payoutCheck?.distributions?.reduce((prev, current) =>
    (current.sequence > prev.sequence) ? current : prev
  );

  // Sort distributions by sequence and separate previous waves from current
  const sortedDistributions = payoutCheck?.distributions
    ? [...payoutCheck.distributions].sort((a, b) => a.sequence - b.sequence)
    : [];
  const previousWaves = sortedDistributions.filter(
    (dist) => dist.sequence !== currentDistribution?.sequence
  );
  const hasPreviousWaves = previousWaves.length > 0;

  // Check if there are any withheld amounts to show Holdback accordion
  const hasWithheldAttorneyFees = (currentDistribution?.attorneysFees?.totalWithheld ?? 0) > 0;
  const hasWithheldDeductions = (currentDistribution?.deductions?.totalWithheld ?? 0) > 0;
  const shouldShowHoldback = hasWithheldAttorneyFees || hasWithheldDeductions;


  return (

    <>
      {payoutCheck?.validationError && (
        <Box className={styles.classes.errorMessageContainer}>
          <Typography className={styles.classes.errorMessageText}>
            {payoutCheck.validationError}
          </Typography>
        </Box>
      )}

      {!!payoutCheck?.warningMessages?.length && (
        <Box className={styles.classes.errorMessageContainer}>
          <Box display="flex" flexDirection="column">
            {payoutCheck.warningMessages.map((message, index) => (
              <Typography key={index} className={styles.classes.warningMessageText}>
                {message}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      <Box className={styles.classes.feesSummaryHeader}>
        <Typography className={styles.classes.feesSummaryHeadline}>Settlement Preview</Typography>
        {payoutCheck !== undefined && (
          <Box className={styles.classes.feesSummaryHeaderPrintBlock}>
            <IconButton
              className={styles.classes.printButton}
              onClick={() => printCheck(printCheckInlineStyles)}
              disableRipple
              disabled={isPayoutCheckUpdating || payoutCheck === undefined}
            >
              <PrintIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box className={styles.classes.pageContainer}>
        <FormProvider {...formMethods}>
          <form className={styles.classes.pageContainer}>
            <Box className={styles.classes.formContainer}>
              <Box className={styles.classes.topRowContainer}>
                <Box className={styles.classes.summaryWrapper} id='printContent'>
                  {payoutCheck !== undefined &&
                    (<Box className={styles.classes.leftColumnsWrapperAmount}>
                      <Box className={styles.classes.formLeftColumnContainerSettledAmount}>
                        {/* Row 1: Total Settlement Amount */}
                        {/* Total Settlement Amount row: visible on screen, hidden in print */}
                        <Box className={clsx(styles.classes.settlementRowBox, 'print-feesSummaryTotalGrossHeadline', 'hide-in-print')}>
                          <Typography className={styles.classes.settlementLabelPrimary}>
                            Total Settlement Amount
                          </Typography>
                          <Typography className={clsx(styles.classes.settlementValuePrimary, styles.classes.amountAligned)}>
                            {numberToUSDCurrency(payoutCheck?.total)}
                          </Typography>
                        </Box>

                        {/* Previous Waves - Only show if multiple distributions exist */}
                        {hasPreviousWaves && previousWaves.map((wave) => (
                          <Box key={wave.sequence} className={styles.classes.settlementRowBox}>
                            <Box className={styles.classes.settlementInfoIconBox}>
                              <Box
                                onMouseEnter={(e) => handleHoverCardOpen(e, wave)}
                                onMouseLeave={handleHoverCardClose}
                                className={styles.classes.infoIconClickableBox}
                              >
                                <InfoOutlinedIcon className={styles.classes.infoIcon} />
                              </Box>
                              <Typography className={clsx(styles.classes.settlementLabelSecondary, 'print-feesSummaryTotalGrossHeadline', 'hide-in-print')}>
                                Wave {wave.sequence} – Settled Amount
                              </Typography>
                            </Box>
                            <Typography className={clsx(styles.classes.settlementValueSecondary, styles.classes.amountAligned, 'hide-in-print')}>
                              -{numberToUSDCurrency(wave.currentDisbursement ?? 0)}
                            </Typography>
                          </Box>
                        ))}

                        {/* Divider and Balance - Only show if multiple distributions exist */}
                        {hasPreviousWaves && (
                          <>
                            <Box className={styles.classes.settlementDivider} />
                            <Box className={styles.classes.settlementRowBox}>
                              <Typography className={clsx(styles.classes.settlementBalanceLabel, 'hide-in-print')}>
                                Balance Remaining
                              </Typography>
                              <Typography className={clsx(styles.classes.settlementBalanceValue, styles.classes.amountAligned, 'hide-in-print')}>
                                {numberToUSDCurrency(payoutCheck?.balanceRemaining ?? 0)}
                              </Typography>
                            </Box>
                          </>
                        )}

                      </Box>
                    </Box>)
                  }

                  {/* Hover Card for Wave Info */}
                  <WaveSettledAmountHoverCard
                    anchorEl={hoverCardAnchorEl}
                    open={Boolean(hoverCardAnchorEl)}
                    onClose={handleHoverCardClose}
                    onMouseEnter={handleHoverCardMouseEnter}
                    distribution={hoveredWave}
                    total={payoutCheck?.total}
                  />

                  <Box className={styles.classes.leftColumnsWrapper}>
                    <Box className={styles.classes.formLeftColumnContainer}>

                      <Box className={styles.classes.formCheckDetailsContainer}>
                        <SettlementsPayoutCheck
                          setPayoutCheck={setPayoutCheck}
                          payoutCheck={payoutCheck}
                          isPayoutCheckUpdating={isPayoutCheckUpdating}
                          setAdditionalSettlementInfo={setAdditionalSettlementInfo}
                          secondaryFirmsData={entityData?.secondaryFirms}
                          caseSystemName={entityData?.caseSystemName}
                          leadFirmData={entityData?.firm}
                          setIsPayoutCheckUpdating={setIsPayoutCheckUpdating}
                          status={entityData?.status as string}
                          errorMessage={errorMessage}
                        />
                      </Box>

                    </Box>
                  </Box>

                  {payoutCheck !== undefined && shouldShowHoldback && (
                    <>
                      {/* Holdback Accordion */}
                      <Box className={styles.classes.leftColumnsWrapper}>
                        <Accordion className={styles.classes.accordionRoot} expanded={isAccordionOpen}
                          onChange={(_, expanded) => setIsAccordionOpen(expanded)}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={styles.classes.expandIconWhite} />}
                            className={styles.classes.accordionSummary}
                          >
                            <Box className={clsx(styles.classes.accordionHeaderBox, 'print-feesSummaryTotalGrossHeadline')}>
                              <Typography className={styles.classes.holdbackTitle}>
                                Holdback
                              </Typography>
                              <Typography className={clsx(styles.classes.holdbackAmount, styles.classes.amountAligned)}>
                                {numberToUSDCurrency(payoutCheck?.holdback ?? 0)}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails className={styles.classes.accordionDetails}>

                            {/* Sections Stack - Contains Holdback sections with consistent spacing */}
                            <Box className={styles.classes.sectionsStack}>
                              {/* ===== Attorneys' Fees Section - Only show if there are withheld attorney fees ===== */}
                              {hasWithheldAttorneyFees && (
                                <Box
                                  className={clsx(
                                    styles.classes.feesSummarySectionContainer,
                                    'print-feesSummarySectionContainer',
                                  )}
                                >
                                  {/* Section heading */}
                                  <Box
                                    className={clsx(
                                      styles.classes.feesSummarySectionHeadline,
                                      'print-feesSummarySectionHeadline',
                                    )}
                                  >
                                    <Typography className={styles.classes.sectionHeadingText}>
                                      Attorneys’ Fees
                                    </Typography>
                                    <Typography className={clsx(styles.classes.sectionAmountText, styles.classes.amountAligned)}>
                                      {numberToUSDCurrency(currentDistribution?.attorneysFees?.totalWithheld ?? 0)}
                                    </Typography>
                                  </Box>

                                  {/* Divider right after Attorneys' Fees heading */}
                                  <Box
                                    className={clsx(
                                      styles.classes.feesSummaryBlockDivider,
                                      'print-feesSummaryBlockDivider',
                                    )}
                                  ></Box>

                                  {/* Lead Counsel - Withheld (if applicable) */}
                                  {(currentDistribution?.attorneysFees?.leadFirm?.totalWithheld ?? 0) > 0 && (
                                    <Box
                                      className={clsx(
                                        styles.classes.feesSummarySectionInnerHeadline,
                                        'print-feesSummarySectionInnerHeadline',
                                      )}
                                    >
                                      <Typography className={styles.classes.innerItemText}>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemLabel}
                                        >
                                          Lead Counsel -{' '}
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemWarning}
                                        >
                                          Withheld
                                        </Typography>
                                      </Typography>
                                      <Typography className={clsx(styles.classes.innerItemAmount, styles.classes.amountAligned)}>
                                        {numberToUSDCurrency(currentDistribution?.attorneysFees?.leadFirm?.totalWithheld ?? 0)}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Co Counsel - Withheld (if applicable) */}
                                  {(currentDistribution?.attorneysFees?.secondaryFirmsCoCounselTotalWithheld ?? 0) > 0 && (
                                    <Box
                                      className={clsx(
                                        styles.classes.feesSummarySectionInnerHeadline,
                                        'print-feesSummarySectionInnerHeadline',
                                      )}
                                    >
                                      <Typography className={styles.classes.innerItemText}>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemLabel}
                                        >
                                          Co Counsel -{' '}
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemWarning}
                                        >
                                          Withheld
                                        </Typography>
                                      </Typography>
                                      <Typography className={clsx(styles.classes.innerItemAmount, styles.classes.amountAligned)}>
                                        {numberToUSDCurrency(currentDistribution?.attorneysFees?.secondaryFirmsCoCounselTotalWithheld ?? 0)}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Referring Counsel - Withheld (if applicable) */}
                                  {(currentDistribution?.attorneysFees?.secondaryFirmsReferingCounselTotalWithheld ?? 0) > 0 && (
                                    <Box
                                      className={clsx(
                                        styles.classes.feesSummarySectionInnerHeadline,
                                        'print-feesSummarySectionInnerHeadline',
                                      )}
                                    >
                                      <Typography className={styles.classes.innerItemText}>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemLabel}
                                        >
                                          Referring Counsel -{' '}
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemWarning}
                                        >
                                          Withheld
                                        </Typography>
                                      </Typography>
                                      <Typography className={clsx(styles.classes.innerItemAmount, styles.classes.amountAligned)}>
                                        {numberToUSDCurrency(currentDistribution?.attorneysFees?.secondaryFirmsReferingCounselTotalWithheld ?? 0)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* ===== Deductions Section - Only show if there are withheld deductions ===== */}
                              {hasWithheldDeductions && (
                                <Box
                                  className={clsx(
                                    styles.classes.feesSummarySectionContainer,
                                    'print-feesSummarySectionContainer',
                                  )}
                                >
                                  {/* Section heading */}
                                  <Box
                                    className={clsx(
                                      styles.classes.feesSummarySectionHeadline,
                                      'print-feesSummarySectionHeadline',
                                    )}
                                  >
                                    <Typography className={styles.classes.sectionHeadingText}>
                                      Deductions
                                    </Typography>
                                    <Typography className={clsx(styles.classes.sectionAmountText, styles.classes.amountAligned)}>
                                      {numberToUSDCurrency(currentDistribution?.deductions?.totalWithheld ?? 0)}
                                    </Typography>
                                  </Box>

                                  {/* Divider right after Deductions heading */}
                                  <Box
                                    className={clsx(
                                      styles.classes.feesSummaryBlockDivider,
                                      'print-feesSummaryBlockDivider',
                                    )}
                                  ></Box>
                                  {/* Liens - Withheld */}
                                  {(currentDistribution?.deductions?.liensWithheld ?? 0) > 0 && (
                                    <Box
                                      className={clsx(
                                        styles.classes.feesSummarySectionInnerHeadline,
                                        'print-feesSummarySectionInnerHeadline',
                                      )}
                                    >
                                      <Typography className={styles.classes.innerItemText}>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemLabel}
                                        >
                                          Liens -{' '}
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemWarning}
                                        >
                                          Withheld
                                        </Typography>
                                      </Typography>
                                      <Typography className={clsx(styles.classes.innerItemAmount, styles.classes.amountAligned)}>
                                        {numberToUSDCurrency(currentDistribution?.deductions?.liensWithheld ?? 0)}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Services Provider - Withheld */}
                                  {(currentDistribution?.deductions?.serviceProvidersWithheld ?? 0) > 0 && (
                                    <Box
                                      className={clsx(
                                        styles.classes.feesSummarySectionInnerHeadline,
                                        'print-feesSummarySectionInnerHeadline',
                                      )}
                                    >
                                      <Typography className={styles.classes.innerItemText}>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemLabel}
                                        >
                                          Services Provider -{' '}
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemWarning}
                                        >
                                          Withheld
                                        </Typography>
                                      </Typography>
                                      <Typography className={clsx(styles.classes.innerItemAmount, styles.classes.amountAligned)}>
                                        {numberToUSDCurrency(currentDistribution?.deductions?.serviceProvidersWithheld ?? 0)}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Expenses - Withheld */}
                                  {(currentDistribution?.deductions?.advancesWithheld ?? 0) > 0 && (
                                    <Box
                                      className={clsx(
                                        styles.classes.feesSummarySectionInnerHeadline,
                                        'print-feesSummarySectionInnerHeadline',
                                      )}
                                    >
                                      <Typography className={styles.classes.innerItemText}>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemLabel}
                                        >
                                          Expenses -{' '}
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={styles.classes.innerItemWarning}
                                        >
                                          Withheld
                                        </Typography>
                                      </Typography>
                                      <Typography className={clsx(styles.classes.innerItemAmount, styles.classes.amountAligned)}>
                                        {numberToUSDCurrency(currentDistribution?.deductions?.advancesWithheld ?? 0)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    </>
                  )}

                  {/* Disbursement Details Accordion - Always show when payoutCheck exists */}
                  {payoutCheck !== undefined && (
                    <>
                      <Box className={styles.classes.leftColumnsWrapper}>
                        <Accordion className={styles.classes.accordionRoot} expanded={isSummaryAccordionOpen}
                          onChange={(_, expanded) => setIsSummaryAccordionOpen(expanded)}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={styles.classes.expandIconWhite} />}
                            className={styles.classes.accordionSummary}
                          >
                            <Box className={clsx(styles.classes.accordionHeaderBox, 'print-feesSummaryTotalGrossHeadline')}>
                              <Box className={styles.classes.accordionHeaderInnerBox}>
                                <Typography>IOLTA Summary</Typography>
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails className={styles.classes.accordionDetails}>
                            <Box className={styles.classes.summaryBox}>

                              {/* Row 1 */}
                              <Box className={clsx(styles.classes.summaryRow, 'print-feesSummarySectionInnerHeadline')}>
                                <Typography className={styles.classes.summaryLabel}>
                                  Starting Balance (this settlement):
                                </Typography>

                                <Typography className={styles.classes.summaryValue}>
                                  {numberToUSDCurrency(payoutCheck?.total ?? 0)}
                                </Typography>
                              </Box>

                              {/* Row 2 */}
                              <Box className={clsx(styles.classes.summaryRow, 'print-feesSummarySectionInnerHeadline')}>
                                <Typography className={styles.classes.summaryLabel}>
                                  Total Previously Disbursed:
                                </Typography>

                                <Typography className={styles.classes.summaryValue}>
                                  {numberToUSDCurrency(payoutCheck?.previousDisbursement ?? 0)}
                                </Typography>
                              </Box>

                              {/* Row 3 */}
                              <Box className={clsx(styles.classes.summaryRow, 'print-feesSummarySectionInnerHeadline')}>
                                <Typography className={styles.classes.summaryLabel}>
                                  {`Wave ${currentWave} Disbursement:`}
                                </Typography>

                                <Typography className={styles.classes.summaryValue}>
                                  -{numberToUSDCurrency(payoutCheck?.currentDisbursement ?? 0)}
                                </Typography>
                              </Box>
                            </Box>

                            <Box className={clsx(styles.classes.summaryBalanceRow, 'print-feesSummaryTotalGrossHeadline')}>
                              <Typography className={styles.classes.ioltaBalanceLabel}>
                                IOLTA Balance after Wave {currentWave}
                              </Typography>

                              <Typography className={styles.classes.iotaBalanceValue}>
                                {numberToUSDCurrency(payoutCheck?.ioltaBalance ?? 0)}
                              </Typography>
                            </Box>

                            {/* Subtitle below IOLTA Balance */}
                            <Box>
                              <Typography className={clsx(styles.classes.subtitleText, 'print-feesSummarySectionInnerHeadline')}>
                                The holdback will stay in IOLTA for future settlements.
                              </Typography>
                            </Box>


                          </AccordionDetails>
                        </Accordion>
                      </Box>
                      <Box className={clsx('print-date-time', styles.classes.printHeaderHidden)}>
                        <Typography>{`Generated at ${currentDateTime[0]} on ${currentDateTime[1]}`}</Typography>
                      </Box>
                    </>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', width: '420px' }}>
                  <Box className={styles.classes.formActionsContainer}>
                    <FieldHeadline>Actions</FieldHeadline>
                    {canExecutePayment && (
                      <Box mt="16px">
                        <Tooltip title={isOnboardingReadOnly ? onboardingLockedTooltip : ''} arrow>
                          <span>
                            <CommonButton
                              disabled={
                                isOnboardingReadOnly ||
                                payoutCheck === undefined ||
                                payoutCheck?.validationError !== null ||
                                isAccountingSyncBlocked || canInitiatePayment
                              }
                              // disabled ={false}
                              type="success"
                              buttonText="Pay Settlement"
                              onButtonClick={handlePaySettlement}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                    {canDelete && (
                      <Box mt="16px">
                        <Tooltip title={isOnboardingReadOnly ? onboardingLockedTooltip : ''} arrow>
                          <span>
                            <CommonButton
                              disabled={isOnboardingReadOnly || entityData?.status !== 'Draft'}
                              type="error"
                              buttonText="Delete"
                              onButtonClick={handleOpenDeletePopup}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                    <Box mt="16px">
                      <CommonButton
                        disabled={payoutCheck === undefined}
                        type="infoMain"
                        buttonText="View Document"
                        onButtonClick={handleViewDocument}
                      />
                    </Box>
                  </Box>

                  {payoutCheck !== undefined && approvalRequired && (<Box className={styles.classes.formReviewContainer}>
                    <ReviewApprovalSection
                      settlementId={settlementId ? Number(settlementId) : 0}
                      refreshSettlement={handleCalculatePayoutInfo}
                      settlementDetails={settlementDetails}
                      settlement={approvalFlowDetails}
                    />
                  </Box>)}
                </Box>
              </Box>

              {/* 4 Equal-width Horizontal Cards Section */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px',
                  width: '95%',
                  mt: '24px',
                }}
              >
              </Box>

              {payoutCheck !== undefined && <>
                <Box className={styles.classes.formLeftDeductionContainer}>
                  {/* {!!deductionsFields?.length && ( */}
                  <SettlementsAttorneyTable
                    totalAttorneyFees={attorneyFeesTotal}
                    settlementId={settlementId ? Number(settlementId) : undefined}
                    canEdit={canEditSettlement}
                    isOnboardingReadOnly={isOnboardingReadOnly}
                    handleMenuOptionClick={handleAttorneyMenuOptionClick}
                    onSyncStateChange={setAttorneyFeesSyncMap}
                    onDispositionChange={() => {
                      setTimeout(() => {
                        handleCalculatePayoutInfo();
                      }, 300);
                    }}
                    onAttorneyFeeUpdate={(updatedAttorneyFee) => {
                      const updatedAttorneyFees = attorneysFeesFields?.map((attorneyFee) =>
                        isSameAttorneyFee(attorneyFee, updatedAttorneyFee)
                          ? {
                            ...attorneyFee,
                            disposition: updatedAttorneyFee.disposition,
                            paymentMethod: updatedAttorneyFee.paymentMethod,
                            externalPaymentDate: updatedAttorneyFee.externalPaymentDate,
                            externalPaymentMemo: updatedAttorneyFee.externalPaymentMemo,
                            externalPaymentReference: updatedAttorneyFee.externalPaymentReference,
                          }
                          : attorneyFee,
                      );

                      setValue('attorneysFees', updatedAttorneyFees, {
                        shouldDirty: false,
                        shouldValidate: false,
                      });
                    }}
                    caseId={Number(currentCaseId)}
                    sequenceNumber={(netSettlementConfig as any)?.sequenceNumber} // eslint-disable-line @typescript-eslint/no-explicit-any
                    onSuccess={() => {
                      handleCalculatePayoutInfo();
                      refreshSettlement();
                    }}
                  />
                  {/* )} */}
                </Box>

                <Box className={styles.classes.formLeftDeductionContainer}>
                  {/* {!!deductionsFields?.length && ( */}
                  <SettlementsDeductionsTable
                    fields={deductionFields}
                    disabled={!canEditSettlement || isPayoutCheckUpdating || payoutCheck === undefined}
                    canEdit={canEditSettlement}
                    canCreate={canCreateSettlement}
                    isOnboardingReadOnly={isOnboardingReadOnly}
                    handleMenuOptionClick={handleDeductionMenuOptionClick}
                    deductionTotal={deductionTotal}
                    settlementId={settlementId ? Number(settlementId) : undefined}
                    onSuccess={() => {
                      setIsEditDeductionPopupOpen(false);
                      setSelectedDeductionIndex(null);
                      setTimeout(() => {
                        handleCalculatePayoutInfo();
                      }, 300);
                    }}
                    onCancel={() => {
                      setIsEditDeductionPopupOpen(false);
                      setSelectedDeductionIndex(null);
                    }}
                    intentType={entityData?.withheld ? 'Withhold' : 'Pay'}
                  />
                </Box>

                <Box className={styles.classes.formLeftDeductionContainer}>
                  <NetSettlementTable
                    handleMenuOptionClick={handleNetSettlementMenuOptionClick}
                    canEdit={canEditSettlement}
                    isOnboardingReadOnly={isOnboardingReadOnly}
                    settlementSummary={settlementSummary}
                    settledAmount={settledAmount}
                    pipAmount={payoutCheck?.pipAmount}
                    lienReimbursementAmount={payoutCheck?.lienReimbursementAmount}
                    settlementType={payoutCheck?.settlementType}
                    onSyncStateChange={setNetSettlementSynced}
                    sequenceNumber={(netSettlementConfig as any)?.sequenceNumber} // eslint-disable-line @typescript-eslint/no-explicit-any
                    onSync={() => {
                      setTimeout(() => {
                        handleCalculatePayoutInfo();
                      }, 300);
                    }}
                  />
                </Box>

                <Box display="flex" flexDirection="column" gap="16px" mt="32px">
                  {canEditSettlement && isDirty && <FormCancelButton onClick={handleCancelChanges} />}
                </Box>
              </>
              }
            </Box>
          </form>
        </FormProvider>
        <Popup
          isOpen={isDeletePopupOpen}
          headlineText={POPUP_DESCRIPTIONS.settlementDelete.headlineText}
          contentText={POPUP_DESCRIPTIONS.settlementDelete.contentText}
          onMainButtonClick={handleDeleteSettlement}
          onSecondaryButtonClick={handleCloseDeletePopup}
        />
        <PaySettlementPopup
          isOpen={isPaySettlementPopupOpen}
          settledAmount={String(settlementSummary?.netSettlementTotal ?? 0)}
          onSubmit={(values) => {
            handleClosePaySettlementPopup();
            handleProcessSettlement(values.pin);
          }}
          onCancel={handleClosePaySettlementPopup}
        />
        <EditDeductionPopup
          isOpen={isEditDeductionPopupOpen}
          loading={false}
          canEdit={canEditSettlement}
          onCancel={() => {
            setIsEditDeductionPopupOpen(false);
            setSelectedDeductionIndex(null);
          }}
          onSuccess={() => {
            setIsEditDeductionPopupOpen(false);
            setSelectedDeductionIndex(null);
            handleCalculatePayoutInfo();
            refreshSettlement();
          }}
          // Pass data via additional props if the component supports them
          {...(getSelectedDeductionData() && {
            initialData: getSelectedDeductionData(),
            dataIndex: selectedDeductionIndex,
          })}
          caseId={currentCaseId}
          settlementAmount={payoutCheck?.total}
          isAccountingIntegrated={Boolean(entityData?.isAccountingIntegrated)}
        />
        <EditNetSettlementPopup
          isOpen={isEditNetSettlementPopupOpen}
          loading={false}
          canEdit={canEditSettlement}
          onCancel={() => {
            setIsEditNetSettlementPopupOpen(false);
          }}
          settlementId={settlementId ? Number(settlementId) : 0}
          onSuccess={() => {
            setIsEditNetSettlementPopupOpen(false);
            refreshSettlement();
            handleCalculatePayoutInfo();
          }}
          initialData={netSettlementConfig}
        />

        <EditAttorneyFeePopup
          isOpen={isEditAttorneyFeePopupOpen}
          loading={false}
          canEdit={canEditSettlement}
          onCancel={() => {
            setIsEditAttorneyFeePopupOpen(false);
            setSelectedAttorneyIndex(null);
          }}
          settlementId={settlementId ? Number(settlementId) : 0}
          onSuccess={() => {
            setIsEditAttorneyFeePopupOpen(false);
            setSelectedAttorneyIndex(null);
            refreshSettlement();
            handleCalculatePayoutInfo();
          }}
          {...(getSelectedAttorneyFeeData() && {
            initialData: getSelectedAttorneyFeeData(),
          })}
        />

        <Popup
          isOpen={isOpenConfirmationPopup}
          headlineText={POPUP_DESCRIPTIONS.paySettlement.headlineText}
          contentText={POPUP_DESCRIPTIONS.paySettlement.contentText}
          onMainButtonClick={handlePaySettlementConfirmation}
          onSecondaryButtonClick={() => setOpenConfirmationPopup(false)}
        />



      </Box>
    </>

  );
};

export default SettlementsEditFormContainer;